import { injectable } from "inversify";
import {
    ColorTokens,
    TypographyTokens,
    SpacingTokens,
    AnimationTokens,
    BorderTokens,
    TextStyleTokens,
} from "./tokens";

export interface ThemeManager {
    // Color methods
    getColor(path: string): number;
    getColorString(path: string): string;

    // Typography methods
    getFontFamily(): string;
    getFontSize(size: keyof typeof TypographyTokens.fontSize): string;

    // Spacing methods
    getSpacing(size: keyof typeof SpacingTokens): number;
    getComponentSpacing(key: keyof typeof SpacingTokens.component): number;
    getMenuSpacing(key: keyof typeof SpacingTokens.menu): number;

    // Animation methods
    getDuration(speed: keyof typeof AnimationTokens.duration): number;
    getEasing(type: keyof typeof AnimationTokens.easing): string;

    // Border methods
    getBorderRadius(size: keyof typeof BorderTokens.radius): number;
    getBorderWidth(size: keyof typeof BorderTokens.width): number;
    getGlowSize(size: keyof typeof BorderTokens.glow): number;

    // Text style methods
    getStrokeWidth(size: keyof typeof TextStyleTokens.stroke): number;
    getTextShadow(): { x: number; y: number; blur: number };

    // Utility methods
    getTextStyle(
        size?: keyof typeof TypographyTokens.fontSize,
        color?: string
    ): Phaser.Types.GameObjects.Text.TextStyle;
}

@injectable()
export class DefaultThemeManager implements ThemeManager {
    private colorTokens = ColorTokens;
    private typographyTokens = TypographyTokens;
    private spacingTokens = SpacingTokens;
    private animationTokens = AnimationTokens;
    private borderTokens = BorderTokens;
    private textStyleTokens = TextStyleTokens;

    /**
     * Get color value in hex format (for Phaser GameObjects)
     * @param path - Dot-notation path like "text.primary" or "ui.border"
     * @example themeManager.getColor("text.primary") // returns 0xffffff
     */
    public getColor(path: string): number {
        const color = this.getNestedValue(this.colorTokens, path);
        if (color && typeof color === "object" && "hex" in color) {
            return color.hex;
        }
        console.warn(`Color not found for path: ${path}, using default white`);
        return 0xffffff;
    }

    /**
     * Get color value as string (for HTML/CSS)
     * @param path - Dot-notation path like "text.primary" or "ui.border"
     * @example themeManager.getColorString("text.primary") // returns "#ffffff"
     */
    public getColorString(path: string): string {
        const color = this.getNestedValue(this.colorTokens, path);
        if (color && typeof color === "object" && "string" in color) {
            return color.string;
        }
        console.warn(`Color string not found for path: ${path}, using default #ffffff`);
        return "#ffffff";
    }

    public getFontFamily(): string {
        return this.typographyTokens.fontFamily.primary;
    }

    public getFontSize(size: keyof typeof TypographyTokens.fontSize): string {
        return this.typographyTokens.fontSize[size] || this.typographyTokens.fontSize.base;
    }

    public getSpacing(size: keyof typeof SpacingTokens): number {
        const spacing = this.spacingTokens[size];
        if (typeof spacing === "number") {
            return spacing;
        }
        console.warn(`Spacing not found: ${size}, using md (16px)`);
        return this.spacingTokens.md;
    }

    public getComponentSpacing(key: keyof typeof SpacingTokens.component): number {
        return this.spacingTokens.component[key];
    }

    public getMenuSpacing(key: keyof typeof SpacingTokens.menu): number {
        return this.spacingTokens.menu[key];
    }

    public getDuration(speed: keyof typeof AnimationTokens.duration): number {
        return this.animationTokens.duration[speed];
    }

    public getEasing(type: keyof typeof AnimationTokens.easing): string {
        return this.animationTokens.easing[type];
    }

    /**
     * Get border radius value
     * @param size - Radius size key
     * @returns Border radius in pixels
     */
    public getBorderRadius(size: keyof typeof BorderTokens.radius): number {
        return this.borderTokens.radius[size];
    }

    /**
     * Get border width value
     * @param size - Width size key
     * @returns Border width in pixels
     */
    public getBorderWidth(size: keyof typeof BorderTokens.width): number {
        return this.borderTokens.width[size];
    }

    /**
     * Get glow/shadow size
     * @param size - Glow size key
     * @returns Glow size in pixels
     */
    public getGlowSize(size: keyof typeof BorderTokens.glow): number {
        return this.borderTokens.glow[size];
    }

    /**
     * Get text stroke width
     * @param size - Stroke size key
     * @returns Stroke width in pixels
     */
    public getStrokeWidth(size: keyof typeof TextStyleTokens.stroke): number {
        return this.textStyleTokens.stroke[size];
    }

    /**
     * Get text shadow configuration
     * @returns Shadow configuration object
     */
    public getTextShadow(): { x: number; y: number; blur: number } {
        return this.textStyleTokens.shadow;
    }

    /**
     * Get a pre-configured text style object for Phaser.GameObjects.Text
     * @param size - Font size key (defaults to "base")
     * @param color - Optional color override (defaults to text.primary)
     * @returns TextStyle object for Phaser Text constructor
     */
    public getTextStyle(
        size: keyof typeof TypographyTokens.fontSize = "base",
        color?: string
    ): Phaser.Types.GameObjects.Text.TextStyle {
        return {
            fontFamily: this.getFontFamily(),
            fontSize: this.getFontSize(size),
            color: color || this.getColorString("text.primary"),
        };
    }

    /**
     * Helper to get nested object values using dot notation
     * @param obj - Object to traverse
     * @param path - Dot-separated path (e.g., "text.primary")
     * @returns Value at path or undefined
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split(".").reduce((current, key) => current?.[key], obj);
    }
}
